import { useState } from "react";
import PageContainer from "@/components/PageContainer";
import { Button, Spinner } from "@/components";
import { 
  Plus, 
  Clock, 
  Calendar, 
  Trash2, 
  Edit3, 
  Search, 
  Info
} from "lucide-react";
import { useActiveInternshipQuery } from "@/hooks/api/internship.queries";
import { useWorkLogsQuery, useDeleteWorkLogMutation } from "@/hooks/api/workLog.queries";
import { WorkLogType } from "@/services/workLog.service";
import WorkLogDialog from "@/components/formDialog/WorkLogDialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import CustomAlertDialog from "@/components/CustomAlertDialog";

const WorkLogsPage = () => {
  const { data: activeInternship, isLoading: loadingActive } = useActiveInternshipQuery();
  
  const applicationId = activeInternship?.offer?.application_id;
  const hasActiveInternship = activeInternship?.has_active_internship && !!applicationId;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<WorkLogType | null>(null);
  const [logToDelete, setLogToDelete] = useState<string | null>(null);

  const { data: workLogsData, isLoading: loadingLogs } = useWorkLogsQuery({
    application_id: applicationId,
    limit: 100,
  });

  const logs = workLogsData?.items || [];
  const deleteMutation = useDeleteWorkLogMutation();

  const handleCreate = () => {
    setSelectedLog(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (log: WorkLogType) => {
    setSelectedLog(log);
    setIsDialogOpen(true);
  };

  const handleDelete = () => {
    if (logToDelete) {
      deleteMutation.mutate(logToDelete, {
        onSuccess: () => setLogToDelete(null),
      });
    }
  };

  if (loadingActive) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Spinner className="h-10 w-10 text-indigo-600" />
      </div>
    );
  }

  if (!hasActiveInternship) {
    return (
      <PageContainer className="p-10 flex flex-col items-center justify-center h-[70vh] text-center space-y-8 animate-in fade-in duration-700">
        <div className="w-24 h-24 rounded-[3rem] bg-slate-50 flex items-center justify-center text-slate-300">
          <Info size={48} />
        </div>
        <div className="space-y-3 max-w-md">
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">No Active Internship Found</h2>
          <p className="text-slate-500 font-medium leading-relaxed">
            Work logs can only be recorded for active internship engagements. Please accept an offer or check back once your internship has officially started.
          </p>
        </div>
        <Button 
          onClick={() => window.location.href = "/offers"}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-black px-8 py-6 rounded-2xl shadow-xl shadow-indigo-100"
        >
          Check My Offers
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="p-8 space-y-10 relative overflow-hidden min-h-screen">
      {/* Decorative Blur */}
      <div className="absolute top-0 right-0 -z-10 h-[500px] w-[500px] bg-indigo-500/5 blur-[120px] rounded-full" />

      {/* Header section with Stats */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
             <div className="p-3 rounded-2xl bg-indigo-600 text-white shadow-lg shadow-indigo-100">
                <Clock size={28} />
             </div>
             <div>
                <h1 className="text-4xl font-black text-slate-800 tracking-tight">Work Logs</h1>
                <p className="text-slate-500 font-bold text-sm flex items-center gap-2">
                  Interning at <span className="text-indigo-600 uppercase tracking-wider text-xs">{activeInternship?.offer?.internship_title}</span>
                </p>
             </div>
          </div>
        </div>

        <Button 
          onClick={handleCreate}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-black px-8 py-7 rounded-2xl shadow-2xl shadow-indigo-200 transition-all active:scale-95 flex items-center gap-2"
        >
          <Plus size={24} />
          Record Today's Work
        </Button>
      </div>

      {/* Logs List Area */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-slate-700 flex items-center gap-3">
              Your Entries
              <Badge className="bg-indigo-50 text-indigo-600 border-none rounded-full px-3">{logs.length}</Badge>
            </h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input 
                 placeholder="Search entries..." 
                 className="pl-10 pr-4 py-2 rounded-xl bg-white border border-slate-100 text-sm font-medium focus:ring-2 focus:ring-indigo-600/20 focus:outline-none focus:border-indigo-200 transition-all"
              />
            </div>
        </div>

        {loadingLogs ? (
          <div className="flex justify-center p-20">
            <Spinner className="h-8 w-8 text-indigo-600" />
          </div>
        ) : logs.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {logs.map((log: WorkLogType) => (
              <div 
                key={log.id} 
                className="group relative bg-white border border-slate-50 rounded-[2rem] p-6 shadow-sm hover:shadow-xl hover:shadow-indigo-50 transition-all duration-300 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-2 h-full bg-indigo-600/10 group-hover:bg-indigo-600 transition-colors" />
                
                <div className="flex items-start gap-6 flex-1">
                  <div className="flex flex-col items-center justify-center w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-600">
                    <span className="text-[10px] font-black uppercase">{format(new Date(log.log_date), "MMM")}</span>
                    <span className="text-xl font-black leading-none">{format(new Date(log.log_date), "dd")}</span>
                  </div>
                  
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center gap-3">
                        <Badge variant="outline" className="border-slate-100 text-slate-500 font-black rounded-lg">
                           <Clock size={12} className="mr-1.5" /> {log.hours_worked} Hours
                        </Badge>
                        <Badge className={cn("rounded-lg font-black border uppercase text-[10px]", 
                          log.status === "reviewed" ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-amber-50 text-amber-600 border-amber-100")}>
                          {log.status}
                        </Badge>
                    </div>
                    <div className="space-y-1">
                        <h3 className="font-extrabold text-slate-800 line-clamp-1">{log.tasks_completed}</h3>
                        <p className="text-sm text-slate-500 font-medium line-clamp-2 max-w-2xl leading-relaxed">
                          {log.challenges || "No challenges recorded for this day."}
                        </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end md:self-center">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleEdit(log)}
                    className="h-12 w-12 rounded-xl text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                  >
                    <Edit3 size={20} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => setLogToDelete(log.id)}
                    className="h-12 w-12 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 size={20} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-20 bg-slate-50/50 rounded-[3rem] border border-dashed border-slate-200 space-y-4">
             <div className="w-16 h-16 rounded-3xl bg-white flex items-center justify-center text-slate-300 shadow-sm">
                <Calendar size={32} />
             </div>
             <div className="text-center">
                <p className="text-slate-700 font-black">No work logs found</p>
                <p className="text-slate-400 text-sm font-medium">Record your first entry to track your progress.</p>
             </div>
             <Button onClick={handleCreate} variant="outline" className="rounded-xl font-bold border-indigo-100 text-indigo-600 hover:bg-indigo-50">
                Create First Entry
             </Button>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <WorkLogDialog 
        isOpen={isDialogOpen} 
        onOpenChange={setIsDialogOpen} 
        onCancel={() => setIsDialogOpen(false)}
        initialData={selectedLog}
        applicationId={applicationId || ""}
      />

      <CustomAlertDialog 
        isOpen={!!logToDelete} 
        onOpenChange={(open) => !open && setLogToDelete(null)} 
        title="Delete Work Log?" 
        description="This action cannot be undone. This log entry will be permanently removed." 
        onConfirm={handleDelete} 
        onCancel={() => setLogToDelete(null)} 
        confirmText="Yes, delete entry" 
        cancelText="Cancel" 
      />
    </PageContainer>
  );
};

export default WorkLogsPage;
